// Rename an owned system or restore its generated name (G-60).
import { SYSTEM_NAME_MAX_LENGTH, systemNameSchema } from '@stars-and-ships/api-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { endpoints } from '../../api/endpoints';
import { queryKeys } from '../../api/queries';
import { Button, ErrorNotice, Sheet, TextField } from '../ui';

export function RenameSystemSheet({
  galaxyId,
  system,
  open,
  onClose,
}: {
  galaxyId: string;
  system: { id: string; name: string; generatedName: string };
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(system.name);
  const rename = useMutation({
    mutationFn: (value: string | null) => endpoints.renameSystem(galaxyId, system.id, value),
    onSuccess: async (data) => {
      setName(data.name);
      await Promise.all([
        qc.invalidateQueries({ queryKey: queryKeys.system(galaxyId, system.id) }),
        qc.invalidateQueries({ queryKey: queryKeys.map(galaxyId) }),
        qc.invalidateQueries({ queryKey: queryKeys.galaxyMe(galaxyId) }),
      ]);
      onClose();
    },
  });
  const valid = systemNameSchema.safeParse(name).success;
  return (
    <Sheet open={open} title="Rename system" onClose={onClose}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) rename.mutate(name.trim());
        }}
      >
        <TextField
          label="System name"
          value={name}
          maxLength={SYSTEM_NAME_MAX_LENGTH}
          onChange={(e) => setName(e.target.value)}
        />
        <p className="text-xs text-muted">
          3–24 characters: letters, digits and single spaces. Every commander sees the new name.
        </p>
        {rename.error ? <ErrorNotice error={rename.error} /> : null}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={!valid || rename.isPending}>
            Save
          </Button>
          {system.name !== system.generatedName ? (
            <Button
              variant="secondary"
              disabled={rename.isPending}
              onClick={() => rename.mutate(null)}
            >
              Restore original name
            </Button>
          ) : null}
        </div>
      </form>
    </Sheet>
  );
}
